import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Check, Building2 } from 'lucide-react';
import { m } from 'motion/react';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import {
  fadeInUp,
  fadeInScale,
  staggerContainer,
  staggerChild,
  defaultViewport,
  cardHover,
  getReducedMotionVariants,
} from '@/lib/motion';
import { useSignupEntityType, useSignupUrl } from '@/hooks/useSignupUrl';
import { trackLandingCtaClicked } from '@/lib/analytics';
import { useLandingSectionTracking } from '@/hooks/useLandingSectionTracking';
import { useTranslation } from '@/i18n/i18n';

function RenderFeatureContent({ feature, soonLabel }: { feature: string; soonLabel: string }) {
  const hasPlanned = feature === soonLabel;
  return (
    <>
      <div className="size-5 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Check className="size-3 text-emerald-600 dark:text-emerald-400" />
      </div>
      <span className="text-sm text-foreground">
        {feature}
        {hasPlanned && (
          <span className="ml-1.5 text-[10px] font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide">
            {soonLabel}
          </span>
        )}
      </span>
    </>
  );
}

export const Pricing = () => {
  const shouldReduceMotion = useReducedMotion();
  const signupUrl = useSignupUrl({ source: 'pricing', placement: 'pricing', page: 'home' });
  const signupEntityType = useSignupEntityType();
  const sectionRef = useLandingSectionTracking('pricing');
  const { locale, t } = useTranslation();

  const pricingFeatures = [
    t('pricing.feature1'),
    t('pricing.feature2'),
    t('pricing.feature3'),
    t('pricing.feature4'),
    // pricing.feature5 ("Backup od doradcy podatkowego") removed with the hero
    // WKRÓTCE chip (founder call, 2026-07-03): the feature isn't built, and a
    // green check presented it as included.
    t('pricing.feature6'),
  ];

  return (
    <section
      ref={sectionRef}
      id="pricing"
      aria-labelledby="pricing-heading"
      className="py-16 px-6 md:px-8 bg-background"
    >
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <m.div
          className="text-center mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={defaultViewport}
          variants={shouldReduceMotion ? getReducedMotionVariants(fadeInUp) : fadeInUp}
        >
          <h2 id="pricing-heading" className="text-3xl md:text-5xl font-semibold mb-4">
            {t('pricing.heading')
              .split('<gradient>')
              .map((part, i) => {
                if (i === 0) return part;
                const [gradient, rest] = part.split('</gradient>');
                return <span key={gradient}>{`${gradient}${rest}`}</span>;
              })}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('pricing.subheading')}
          </p>
        </m.div>

        {/* Single free-beta card: no post-beta price is promised */}
        <div className="max-w-md mx-auto pt-4">
          {/* Open beta (highlighted) */}
          <m.div
            initial="hidden"
            whileInView="visible"
            viewport={defaultViewport}
            variants={shouldReduceMotion ? getReducedMotionVariants(fadeInScale) : fadeInScale}
            whileHover={shouldReduceMotion ? undefined : cardHover}
          >
            <Card className="p-8 border border-foreground/20 shadow-sm relative h-full">
              {/* POLECANE ghost badge */}
              <div className="absolute -top-10 left-1/2 -translate-x-1/2 z-10">
                <img
                  src="/sprites/polecane-badge-transparent.webp"
                  alt="Polecane"
                  width={288}
                  height={288}
                  loading="lazy"
                  decoding="async"
                  className="size-24 object-contain drop-shadow-lg"
                />
              </div>

              {/* Badge */}
              <div className="flex justify-center mb-4 pt-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-muted text-muted-foreground">
                  {t('pricing.now')}
                </span>
              </div>

              {/* Plan header */}
              <div className="text-center mb-8">
                <div className="flex items-baseline justify-center gap-1 mb-2">
                  <span className="font-mono tabular-nums text-6xl font-bold">0</span>
                </div>
                <p className="text-sm text-muted-foreground">{t('pricing.priceLine')}</p>
              </div>

              {/* Features list */}
              <m.ul
                className="space-y-4 mb-8"
                initial="hidden"
                whileInView="visible"
                viewport={defaultViewport}
                variants={
                  shouldReduceMotion ? getReducedMotionVariants(staggerContainer) : staggerContainer
                }
              >
                {pricingFeatures.map((feature) => (
                  <m.li
                    key={feature}
                    className="flex items-start gap-3"
                    variants={
                      shouldReduceMotion ? getReducedMotionVariants(staggerChild) : staggerChild
                    }
                  >
                    <div className="size-5 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="size-3 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <span className="text-sm text-foreground">{feature}</span>
                  </m.li>
                ))}
              </m.ul>

              {/* CTA button */}
              <Button
                className="w-full h-14 text-base font-bold rounded-xl shadow-lg shadow-primary/25"
                asChild
              >
                <a
                  href={signupUrl}
                  onClick={() =>
                    trackLandingCtaClicked('pricing_primary', t('pricing.ctaPrimary'), {
                      entity_type: signupEntityType,
                      source: 'pricing',
                      placement: 'pricing',
                      page: 'home',
                      locale,
                    })
                  }
                >
                  {t('pricing.ctaPrimary')}
                </a>
              </Button>
              <p className="text-xs text-muted-foreground text-center mt-4">
                {t('pricing.noCreditCard')}
              </p>
            </Card>
          </m.div>
        </div>

        <p className="text-muted-foreground mx-auto mt-6 max-w-2xl text-center text-sm">
          {t('pricing.honestySubline')}
        </p>

        {/* Sp. z o.o. teaser */}
        <m.div
          className="mt-10 max-w-3xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={defaultViewport}
          variants={shouldReduceMotion ? getReducedMotionVariants(fadeInUp) : fadeInUp}
        >
          <div className="flex items-center gap-4 px-6 py-5 rounded-xl border border-dashed border-border bg-muted/40">
            <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Building2 className="size-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground">
                {t('pricing.spzoo')}{' '}
                <span className="text-[10px] font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wide ml-1">
                  {t('pricing.soon')}
                </span>
              </p>
              <p className="text-sm text-muted-foreground mt-0.5">{t('pricing.spzooDesc')}</p>
            </div>
          </div>
        </m.div>
      </div>
    </section>
  );
};
